<script setup lang="ts">
  import '@/assets/calendar-legal-os.css'
  import CalendarEventChipContent from '@/components/app/calendar/CalendarEventChipContent.vue'
  import { useCalendarMonthGrid } from '@/composables/useCalendarMonthGrid'
  import type { CalendarGridEvent } from '@/types/calendar/grid'
  import {
    CALENDAR_EVENT_CHIP_CLASS,
    type CalendarEventTypeLabels
  } from '@/utils/calendar/event-display'
  import { computed, toRef } from 'vue'

  const props = defineProps<{
    selectedDate: Date
    today: Date
    eventsByDate: Map<string, CalendarGridEvent[]>
    weekdayLabels: string[]
    moreLabel: string
    eventTypeLabels: CalendarEventTypeLabels
  }>()

  const emit = defineEmits<{
    'select-date': [date: Date]
    'open-event': [event: CalendarGridEvent]
  }>()

  const eventsByDateRef = computed(() => props.eventsByDate)
  const selectedDateRef = toRef(props, 'selectedDate')

  const { monthRows, isMonthEmpty, monthEventsFor } = useCalendarMonthGrid(
    selectedDateRef,
    computed(() => props.today),
    eventsByDateRef
  )

  const isWeekend = (idx: number): boolean => idx >= 5

  const onDayClick = (date: Date): void => {
    emit('select-date', date)
  }
</script>

<template>
  <div
    class="calendar-main bg-layer relative flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-width:thin]
      [&::-webkit-scrollbar-thumb]:rounded-none [&::-webkit-scrollbar]:w-2"
    data-cy="calendar-month-grid"
  >
    <div class="border-layer-line bg-layer sticky inset-x-0 top-0 z-[9] border-b">
      <div class="grid grid-cols-7">
        <span
          v-for="d in weekdayLabels"
          :key="d"
          class="bg-layer block px-3 py-2 text-end text-xs text-foreground"
        >
          {{ d }}
        </span>
      </div>
    </div>

    <div
      v-for="(row, ridx) in monthRows"
      :key="ridx"
      class="relative flex h-[125px]"
    >
      <div class="grid size-full grid-cols-7">
        <div
          v-for="(cell, cidx) in row"
          :key="cidx"
          :class="[
            'border-layer-line relative flex flex-1 border-b border-s first:border-s-0',
            isWeekend(cidx)
              ? 'after:bg-surface/40 z-[8] after:pointer-events-none after:absolute after:inset-0 after:-z-[1]'
              : ''
          ]"
        >
          <button
            type="button"
            :class="[
              `focus-visible:bg-surface-focus absolute right-1.5 top-1.5 z-[1] flex min-h-6 min-w-6 items-center
              justify-center rounded-full px-1.5 text-xs focus:outline-none`,
              cell.isSelected
                ? 'border-primary-line border bg-primary text-primary-foreground hover:bg-primary-hover'
                : cell.isToday
                  ? 'hover:bg-surface-hover border border-primary/40 font-medium text-primary'
                  : cell.currentMonth
                    ? 'hover:bg-surface-hover border border-transparent text-foreground'
                    : 'text-muted-foreground-1/60 hover:bg-surface-hover border border-transparent'
            ]"
            @click="onDayClick(cell.date)"
          >
            <span
              v-if="cell.monthLabel"
              class="me-1 font-semibold"
            >
              {{ cell.monthLabel }}
            </span>
            {{ cell.day }}
          </button>

          <div
            v-if="!isMonthEmpty && monthEventsFor(cell).length > 0"
            class="mt-8 w-full space-y-1 p-1"
          >
            <button
              v-for="(ev, i) in monthEventsFor(cell).slice(0, 2)"
              :key="i"
              type="button"
              :class="[
                'relative flex w-full flex-col rounded-sm px-1.5 py-0.5 text-start text-xs',
                CALENDAR_EVENT_CHIP_CLASS
              ]"
              @click.stop="emit('open-event', ev)"
            >
              <CalendarEventChipContent
                :event="ev"
                :event-type-labels="eventTypeLabels"
              />
            </button>
            <button
              v-if="monthEventsFor(cell).length > 2"
              type="button"
              class="text-muted-foreground-1 w-full rounded-sm px-1.5 py-0.5 text-start text-xs font-medium
                hover:text-foreground focus:text-foreground focus:outline-none"
              @click="onDayClick(cell.date)"
            >
              + {{ monthEventsFor(cell).length - 2 }} {{ moreLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
